import { useState } from 'react';
import type React from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Loader } from 'ui/loader';
import { SearchField } from 'ui/search-field';

import Button from 'components/partials/button/button';
import { useFeatureHintTarget } from 'components/shared/feature-hints';
import SessionItem from './session-item';
import TokenUsageIndicator from './token-usage-indicator';

import { useFeatureFlags } from 'components/shared/feature-flags';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useApiGetConversations } from '../../hooks/use-api-get-conversations';
import { useTokenUsage } from '../../hooks/use-token-usage';

import {
  getFallbackResetDate,
  getIsTokenUsageExempt,
} from '../../helpers/token-usage-helpers';

import { GUIDE_FAQ_URL } from '../../ai-assistant.constants';

import Icons from 'assets/icons';

interface Session {
  id: string;
  name: string;
  createdAt: string;
}

interface SessionsSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  conversationId: string | null;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  onFocusMessageInput?: () => void;
  isReadOnly?: boolean;
}

const SessionsSidebar = ({
  isOpen,
  onToggle,
  conversationId,
  setSearchParams,
  clientId,
  onFocusMessageInput,
  isReadOnly,
}: SessionsSidebarProps): React.JSX.Element => {
  const { featureFlags } = useFeatureFlags();
  const isTokenUsageEnabled = featureFlags['ai-token-usage'];
  const currentUser = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const isAdmin = currentUser?.data?.fdUser || currentUser?.data?.clientAdmin;
  const isTokenUsageExempt = getIsTokenUsageExempt(currentUser?.data);
  const hintTargetProps = useFeatureHintTarget('new-chat');
  const usageHintTargetProps = useFeatureHintTarget('token-usage');
  const { percentRemaining, resetDate, hasUsageData, isError, isLoading } =
    useTokenUsage({
      oktaUserId: currentUser?.data?.oktaUserId ?? '',
      enabled: !isTokenUsageExempt,
    });

  const { data: conversations = [], isLoading: isLoadingConversations } =
    useApiGetConversations({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
      searchTerm,
    });

  const conversationsArray = conversations as Session[];
  const hasConversations = conversationsArray.length > 0;

  const handleNewChat = (): void => {
    setSearchParams({});
    onFocusMessageInput?.();
  };

  return (
    <Stack
      sx={[
        {
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 10,
          gap: 2,
          p: 2,
        },
        !!isOpen && {
          height: '100%',
          borderRightWidth: '1px',
          width: '16rem',
        },
        !isOpen && {
          height: '2.75rem',
          borderRightWidth: 0,
          width: '10rem',
        },
      ]}
    >
      <Stack
        direction="row"
        sx={[
          {
            alignItems: 'center',
            color: 'tokens.navy.800',
            transitionProperty: 'gap',
            transitionDuration: '300ms',
            transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
          },
          !!isOpen && { justifyContent: 'space-between', gap: 1 },
          !isOpen && { justifyContent: 'flex-start', gap: 0.5 },
        ]}
      >
        <Button
          variant="reserve-text"
          className="!size-10 !p-0"
          onClick={onToggle}
          aria-label="Toggle AI Assistant sidebar"
        >
          <Icons.AiSidebarLeft customClass="w-5 h-5" />
        </Button>

        <Button
          {...hintTargetProps}
          variant="reserve-text"
          className={classNames(hintTargetProps.className)}
          iconBefore={<Icons.AiChatNew />}
          onClick={handleNewChat}
          disabled={isReadOnly}
        >
          {isOpen ? 'New chat' : ''}
        </Button>
      </Stack>

      <Box
        sx={[
          {
            display: 'grid',
            minHeight: 0,
            flex: '1 1 0%',
            transitionProperty: 'grid-template-rows, opacity',
            transitionDuration: '300ms',
            transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
          },
          !!isOpen && { gridTemplateRows: '1fr', opacity: 1 },
          !isOpen && { gridTemplateRows: '0fr', opacity: 0 },
        ]}
      >
        <Stack sx={{ minHeight: 0, minWidth: 0, gap: 2 }}>
          <Stack sx={{ minHeight: 0, flex: '1 1 0%', gap: 2, pt: 1 }}>
            <SearchField placeholder="Search" onSearch={setSearchTerm} />

            <Stack sx={{ minHeight: 0, flex: '1 1 0%' }}>
              <h4 className="shrink-0 font-semibold text-gray-700 px-2">
                Session history
              </h4>

              <div className="relative overflow-y-auto h-full scrollbar-thin">
                {hasConversations && !isLoadingConversations ? (
                  <Stack sx={{ gap: 0.5, minHeight: 0, flex: '1 1 0%' }}>
                    {conversationsArray.map((session) => (
                      <SessionItem
                        key={session.id}
                        session={session}
                        isActive={session.id === conversationId}
                        setSearchParams={setSearchParams}
                        clientId={clientId}
                        isReadOnly={isReadOnly}
                      />
                    ))}
                  </Stack>
                ) : isLoadingConversations ? (
                  <Loader
                    loading
                    fullScreen={false}
                    backdrop="white"
                    overlay="white"
                  />
                ) : (
                  <Box sx={{ p: 1 }}>
                    <p className="text-sm text-gray-600 font-medium">
                      No chats yet
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-0.5 flex-wrap">
                      Start a
                      <Button
                        variant="reserve-text-link"
                        size="xs"
                        className="p-0"
                        onClick={handleNewChat}
                        disabled={isReadOnly}
                      >
                        new chat
                      </Button>
                      .
                    </p>
                  </Box>
                )}
              </div>
            </Stack>
          </Stack>

          {!isTokenUsageExempt &&
            (!isTokenUsageEnabled || isLoading || hasUsageData || isError) && (
              <div
                {...usageHintTargetProps}
                className={classNames(
                  'shrink-0',
                  usageHintTargetProps.className
                )}
              >
                <TokenUsageIndicator
                  percentRemaining={
                    isTokenUsageEnabled ? percentRemaining : 100
                  }
                  resetDate={
                    isTokenUsageEnabled ? resetDate : getFallbackResetDate()
                  }
                  hasError={isTokenUsageEnabled ? isError : false}
                  isLoading={isTokenUsageEnabled ? isLoading : false}
                />
              </div>
            )}

          {!isAdmin && (
            <Button
              variant="reserve-outlined"
              iconBefore={<Icons.OpenBook />}
              className="shrink-0 w-full"
              onClick={() => window.open(GUIDE_FAQ_URL, '_blank')}
            >
              Guide & FAQ
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default SessionsSidebar;
